import {
    DEFAULT_STATE as defaultReduxSateUserVariantTest,
    State as UserVarianTestState,
} from '@/redux/modules/userVariantTest';
import all from '@/intl/all';
import checkUserAgentForBot from '@/utils/checkUserAgentForBot';
import Cookies from 'universal-cookie';
import { DEFAULT_STATE as defaultReduxStateAnalytics } from '@/redux/modules/analytics';
import { DEFAULT_STATE as defaultReduxStateConfig } from '@/redux/modules/config';
import { DEFAULT_STATE as defaultReduxStateIntl } from '@/redux/modules/intl';
import { DEFAULT_STATE as defaultReduxStateUser } from '@/redux/modules/user';
import { DEFAULT_STATE as defaultReduxStateWindowSize } from '@/redux/modules/windowSize';
import { EmptyUserData } from '@/types/UserData';
import { getBrowserDataFromUA } from './modules/browser';
import { getPageSizeFromCookie } from '@/utils/pagination';
import { GlobalState } from '@/redux/rootReducer';

const cookie = new Cookies();

const DEFAULT_ITEMS_TO_SHOW = 24;

type GetInitialDataParams = {
    basename?: string;
    config?: any;
    serverRequest?: {
        hostname: string;
        paginationPageSizeCookie: number | 'all';
        referrer: string;
        url: string;
        userVariantTest: UserVarianTestState;
        useragent: string;
        whiteLabelInfo?: {
            logoId: string;
            name: string;
            sellerId: string;
            website: string;
        };
    };
};

const getInitialData = ({
    basename = '',
    config: configDefaults = {},
    serverRequest,
}: GetInitialDataParams): Partial<GlobalState> => {
    // check the cookie for a valid token
    const referrer = serverRequest?.referrer || '';
    const touchDate = new Date().getTime();
    const disableModalForTesting =
        serverRequest?.url.includes('hideForTesting') || serverRequest?.url.includes('/console/');

    var whiteLabel: any = {
        hostname: serverRequest?.hostname,
        isWhiteLabel: false,
        logo: 'https://p1.liveauctioneers.com/dist/images/logo.svg',
        sellerId: 0,
    };

    const pageSize = serverRequest
        ? getPageSizeFromCookie(serverRequest?.paginationPageSizeCookie)
        : getPageSizeFromCookie(cookie.get('pagination-page-size')) || DEFAULT_ITEMS_TO_SHOW;

    if (serverRequest?.whiteLabelInfo) {
        const { logoId, name: sellerName, sellerId, website } = serverRequest.whiteLabelInfo;
        const siteNameMatch = (website || '').match(/^(?:https?:\/\/)?(?:[w]{3}\.)?(.*)$/);
        const siteName = siteNameMatch && siteNameMatch[1].length > 0 ? siteNameMatch[1].replace(/\/+$/, '') : '';

        whiteLabel = {
            hostname: serverRequest?.hostname,
            isWhiteLabel: true,
            logo:
                !logoId || logoId === ''
                    ? null
                    : `https://images.liveauctioneers.com/houses/logos/lg/${logoId}_large.jpg?auto=webp&format=pjpg`,
            name: sellerName,
            sellerId,
            siteName,
            siteUrl: website || '',
        };
    }

    let intl: typeof defaultReduxStateIntl = {
        locale: 'en',
        messages: all['en'],
    };

    if (basename === '/en-gb') {
        intl = {
            locale: 'en-gb',
            messages: { ...all['en'], ...all['en-gb'] },
        };
    }

    const config = {
        ...defaultReduxStateConfig,
        ...configDefaults,
        baseUrl: `${configDefaults.baseUrl}${basename}`,
        wwwBaseUrl: configDefaults.baseUrl,
    };

    /**
     * Use the useragent string to guess at what the screen size is, used to toggle mobile/desktop styles
     */
    let width = 1024;
    const userAgent = serverRequest?.useragent || (typeof navigator !== 'undefined' ? navigator.userAgent : '');
    const bot = checkUserAgentForBot(userAgent);
    const { deviceType, isOutdated, majorVersion, microsoftBrowser, mobile, name } = getBrowserDataFromUA(userAgent);

    if (Boolean(mobile)) {
        if (mobile === 'phone') {
            width = 500;
        } else if (mobile === 'tablet') {
            width = 768;
        }
    }

    // If on the client side, use the actual window width
    if (!serverRequest) {
        width = window.innerWidth;
    }

    return {
        analytics: {
            ...defaultReduxStateAnalytics,
            bot,
            referrer,
            touchDate,
        },
        autoPopModal: {
            dateLastShown: '',
            disableModalForTesting,
            hasLoggedInBefore: false,
        },
        browser: {
            deviceType,
            isOutdated,
            majorVersion,
            microsoftBrowser,
            mobile,
            name,
            userAgentString: userAgent,
        },
        config,
        intl,
        pagination: {
            pageSize,
        },
        user: { ...defaultReduxStateUser, userData: EmptyUserData },
        userVariantTest: serverRequest?.userVariantTest || defaultReduxSateUserVariantTest,
        whiteLabel,
        windowSize: { ...defaultReduxStateWindowSize, initialWidth: width, isMobile: Boolean(mobile), width: width },
    };
};

export default getInitialData;
